/**
 * Returns the full month name from a date string or Date object.
 * @param date - A date string or Date object
 * @returns The full month name (e.g., "January", "February") or empty string if invalid
 */
export const getMonthName = (
  date: string | Date | null | undefined,
): string => {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toLocaleString('en-GB', { month: 'long' });
};

/**
 * Returns the ordinal suffix for a day number (st, nd, rd, th)
 */
const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

/**
 * Formats a draw date as "Month DDth at HHam/pm" (e.g., "September 30th at 9pm")
 * @param date - A date string or Date object
 * @returns Formatted date string or empty string if invalid
 */
export const formatDrawDate = (
  date: string | Date | null | undefined,
): string => {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const month = dateObj.toLocaleString('en-GB', { month: 'long' });
  const day = dateObj.getDate();
  const hours = dateObj.getHours();
  const period = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;

  return `${month} ${day}${getOrdinalSuffix(day)} at ${hour12}${period}`;
};

/**
 * Formats a date as "Mon DD" (e.g., "Sep 30")
 * @param date - A date string or Date object
 * @returns Formatted date string or empty string if invalid
 */
export const formatShortDate = (
  date: string | Date | null | undefined,
): string => {
  if (!date) {
    return '';
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const month = dateObj.toLocaleString('en-GB', { month: 'short' });
  const day = dateObj.getDate();

  return `${month} ${day}`;
};
